import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Drawer, Empty, Input, Modal, Popconfirm, Result, Select, Space, Spin, Switch, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SaveOutlined, PlayCircleOutlined, RocketOutlined, RollbackOutlined, CodeOutlined, ApartmentOutlined, RobotOutlined } from '@ant-design/icons';
import {
  aiDraftRuleChain, createRuleChain, deleteRuleChain, fetchRuleChain, fetchRuleChains, fetchRuleChainRuns, fetchRuleChainRunDetail, publishRuleChain, rollbackRuleChain,
  saveRuleChainDraft, testRuleChain, updateRuleChain,
  type RuleChainAiDraftResult, type RuleChainGraph, type RuleChainItem, type RuleChainNode, type RuleChainNodeType, type RuleChainRunItem, type RuleChainTestResult, type RuleChainVersionItem,
} from '../api/ruleChains';
import RuleChainCanvas from '../components/rule-chain/RuleChainCanvas';
import RuleNodeConfigPanel from '../components/rule-chain/RuleNodeConfigPanel';
import { fetchEntitlement } from '../utils/entitlement';
import { RULE_NODE_CATEGORIES, RULE_NODE_DEFINITIONS, getRuleNodeDefinition } from '@dtwin/shared';
import { usePopupStore } from '../stores/popupStore';
import useAuthStore from '../stores/authStore';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const DEFAULT_PAYLOAD = '{\n  "imei": "TEST0001",\n  "data": { "v": 7 }\n}';

type PaletteItem = { type: RuleChainNodeType; kind?: string; label: string; color: string; category: string };

const LEGACY_PALETTE: PaletteItem[] = [
  { type: 'source', label: '触发', color: '#1677ff', category: 'source' },
  { type: 'filter', label: '脚本过滤', color: '#fa8c16', category: 'filter' },
  { type: 'transform', label: '脚本转换', color: '#722ed1', category: 'transform' },
  { type: 'enrich', label: '设备补充', color: '#13c2c2', category: 'enrich' },
  { type: 'action', label: '告警', color: '#52c41a', category: 'action' },
  { type: 'action', kind: 'broadcast', label: '广播', color: '#52c41a', category: 'action' },
  { type: 'action', kind: 'webhook', label: 'Webhook', color: '#52c41a', category: 'action' },
  { type: 'action', kind: 'command', label: '设备命令', color: '#52c41a', category: 'action' },
  { type: 'route', label: '分支', color: '#eb2f96', category: 'flow' },
  { type: 'debug', label: '调试', color: '#8c8c8c', category: 'debug' },
];

const NODE_PALETTE: PaletteItem[] = [
  ...LEGACY_PALETTE,
  ...RULE_NODE_DEFINITIONS.map((d) => ({
    type: d.type as RuleChainNodeType,
    kind: d.kind,
    label: d.title,
    color: RULE_NODE_CATEGORIES.find((c) => c.id === d.category)?.color || '#8c8c8c',
    category: d.category,
  })),
];
const PALETTE_CATEGORY_LABEL: Record<string, string> = {
  source: '触发',
  ...Object.fromEntries(RULE_NODE_CATEGORIES.map((c) => [c.id, c.label])),
};
const PALETTE_CATEGORY_ORDER = ['source', ...RULE_NODE_CATEGORIES.map((c) => c.id)];

function pretty(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function graphDiff(base: RuleChainGraph | undefined, next: RuleChainGraph | undefined) {
  const a = base || EMPTY_GRAPH;
  const b = next || EMPTY_GRAPH;
  const aNodes = new Map((a.nodes || []).map((n) => [n.id, n]));
  const bNodes = new Map((b.nodes || []).map((n) => [n.id, n]));
  const added = [...bNodes.keys()].filter((id) => !aNodes.has(id));
  const removed = [...aNodes.keys()].filter((id) => !bNodes.has(id));
  const changed = [...bNodes.keys()].filter((id) => aNodes.has(id) && JSON.stringify(aNodes.get(id)) !== JSON.stringify(bNodes.get(id)));
  const edgeKey = (e: any) => `${e.from}->${e.to}:${e.fromPort || e.condition || ''}`;
  const aEdges = new Set((a.edges || []).map(edgeKey));
  const bEdges = new Set((b.edges || []).map(edgeKey));
  return {
    added, removed, changed,
    edgesAdded: [...bEdges].filter((k) => !aEdges.has(k)),
    edgesRemoved: [...aEdges].filter((k) => !bEdges.has(k)),
  };
}

const EMPTY_GRAPH: RuleChainGraph = { nodes: [], edges: [] };

export default function RuleChains() {
  const [items, setItems] = useState<RuleChainItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [chain, setChain] = useState<(RuleChainItem & { versions: RuleChainVersionItem[] }) | null>(null);
  const [graphObj, setGraphObj] = useState<RuleChainGraph>(EMPTY_GRAPH);
  const [graphText, setGraphText] = useState('');
  const [tab, setTab] = useState<'canvas' | 'json' | 'runs'>('canvas');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [payloadText, setPayloadText] = useState(DEFAULT_PAYLOAD);
  const [testResult, setTestResult] = useState<RuleChainTestResult | null>(null);
  const [rollbackId, setRollbackId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState<boolean | null>(null);
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const canPublish = role !== 'viewer';
  const [runs, setRuns] = useState<RuleChainRunItem[]>([]);
  const [runDetail, setRunDetail] = useState<any>(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiRequirement, setAiRequirement] = useState('');
  const [aiPayload, setAiPayload] = useState(DEFAULT_PAYLOAD);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<RuleChainAiDraftResult | null>(null);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [diffVersion, setDiffVersion] = useState<RuleChainVersionItem | null>(null);
  const [marketOpen, setMarketOpen] = useState(false);
  const [marketQuery, setMarketQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchRuleChains();
      setItems(r.items || []);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    void fetchEntitlement().then((e) => {
      if (!alive) return;
      const p = isAdmin ? true : (e?.paid ?? true);
      setPaid(p);
      if (p) void load();
    });
    return () => { alive = false; };
  }, [isAdmin]);

  const draft = useMemo(() => chain?.versions?.find((v) => v.status === 'draft'), [chain]);
  const current = useMemo(() => chain?.versions?.find((v) => v.id === chain?.currentVersionId), [chain]);

  const setGraph = (g: RuleChainGraph) => {
    setGraphObj(g);
    setGraphText(pretty(g));
    setTestResult(null);
  };

  const openEditor = async (id: string) => {
    setBusy(true);
    try {
      const c = await fetchRuleChain(id);
      setChain(c);
      const v = c.versions?.find((x) => x.status === 'draft') || c.versions?.find((x) => x.id === c.currentVersionId);
      const g = (v?.graph || EMPTY_GRAPH) as RuleChainGraph;
      setGraphObj(g);
      setGraphText(pretty(g));
      setTestResult(null);
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setRollbackId(c.versions?.find((x) => x.status === 'published')?.id);
      setRuns([]);
      setRunDetail(null);
      setEditorOpen(true);
    } catch (e: any) {
      message.error(e?.message || '加载规则链失败');
    } finally {
      setBusy(false);
    }
  };

  const parseGraph = (): RuleChainGraph | null => {
    try {
      const g = JSON.parse(graphText);
      if (!g || typeof g !== 'object') throw new Error('图必须是对象');
      return g as RuleChainGraph;
    } catch (e: any) {
      message.error(`图 JSON 非法：${e?.message || e}`);
      return null;
    }
  };

  const addNode = (type: RuleChainNodeType, kind?: string) => {
    if (type === 'source' && graphObj.nodes.some((n) => n.type === 'source')) {
      message.warning('已有 source 节点（只能一个）');
      return;
    }
    const def = kind ? getRuleNodeDefinition(type, kind) : undefined;
    const id = `${kind || type}-${Date.now().toString(36)}`;
    const config: Record<string, unknown> = def
      ? { ...def.defaults }
      : type === 'source'
        ? { triggerType: 'device' }
        : type === 'action'
          ? { type: kind || 'broadcast', params: kind === 'webhook' ? { url: '' } : kind === 'command' ? { action: 'get_status' } : { event: 'rulechain:triggered' } }
          : type === 'enrich'
            ? { source: 'device' }
            : {};
    const node: RuleChainNode = {
      id,
      type,
      ...(kind ? { kind } : {}),
      config,
      position: { x: 80 + (graphObj.nodes.length % 3) * 230, y: 60 + Math.floor(graphObj.nodes.length / 3) * 150 },
    };
    setGraph({ ...graphObj, nodes: [...graphObj.nodes, node] });
    setSelectedNodeId(id);
    setSelectedEdgeId(null);
  };

  const saveDraft = async () => {
    if (!chain) return;
    const g = parseGraph();
    if (!g) return;
    setBusy(true);
    try {
      await saveRuleChainDraft(chain.id, g);
      message.success('草稿已保存');
      await openEditor(chain.id);
    } catch (e: any) {
      message.error(`保存失败：${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (!chain) return;
    let payload: unknown;
    try {
      payload = JSON.parse(payloadText);
    } catch (e: any) {
      message.error(`样例报文 JSON 非法：${e?.message || e}`);
      return;
    }
    setBusy(true);
    try {
      const r = await testRuleChain(chain.id, payload);
      setTestResult(r);
      message[r.ok ? 'success' : 'warning'](r.ok ? '试跑通过' : `试跑失败：${r.error || '未知错误'}`);
    } catch (e: any) {
      message.error(`试跑失败：${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!chain) return;
    setBusy(true);
    try {
      const r = await publishRuleChain(chain.id);
      message.success(`已发布 v${r.version}`);
      await openEditor(chain.id);
      await load();
    } catch (e: any) {
      message.error(`发布失败：${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!chain || !rollbackId) return;
    setBusy(true);
    try {
      const r = await rollbackRuleChain(chain.id, rollbackId);
      message.success(`已回滚到 v${r.version}`);
      await openEditor(chain.id);
      await load();
    } catch (e: any) {
      message.error(`回滚失败：${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row: RuleChainItem, enabled: boolean) => {
    try {
      await updateRuleChain(row.id, { enabled });
      setItems((prev) => prev.map((x) => (x.id === row.id ? { ...x, enabled } : x)));
    } catch (e: any) {
      message.error(e?.message || '更新失败');
    }
  };

  const loadRuns = async () => {
    if (!chain) return;
    setRunsLoading(true);
    try {
      const r = await fetchRuleChainRuns(chain.id);
      setRuns(r.items || []);
    } catch (e: any) {
      message.error(e?.message || '加载运行历史失败');
    } finally {
      setRunsLoading(false);
    }
  };

  const openRun = async (runId: string) => {
    try {
      const d = await fetchRuleChainRunDetail(runId);
      setRunDetail(d);
    } catch (e: any) {
      message.error(e?.message || '加载运行详情失败');
    }
  };

  const highlightRun = () => {
    if (!runDetail) return;
    const logs = runDetail.logs || [];
    setTestResult({
      ok: runDetail.run.status === 'success',
      status: runDetail.run.status === 'success' ? 'success' : 'failed',
      nodeResults: logs.map((l: any) => ({
        nodeId: l.nodeId,
        type: l.nodeType,
        status: l.status,
        input: l.input,
        output: l.output,
        error: l.error || undefined,
        durationMs: l.durationMs,
      })),
      output: logs[logs.length - 1]?.output,
      error: runDetail.run.error || undefined,
      durationMs: runDetail.run.durationMs,
    });
    setTab('canvas');
    message.success('已在画布高亮该次运行');
  };

  const runsTab = (
    <div data-testid="rule-chain-runs">
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" onClick={loadRuns} loading={runsLoading} data-testid="rule-chain-runs-refresh">刷新</Button>
        <Text type="secondary" style={{ fontSize: 12 }}>最近 50 次运行；点行查看节点日志</Text>
      </Space>
      <Table
        size="small"
        rowKey="id"
        loading={runsLoading}
        dataSource={runs}
        pagination={false}
        onRow={(row) => ({ onClick: () => void openRun(row.id), style: { cursor: 'pointer' } })}
        columns={[
          { title: '时间', dataIndex: 'createdAt', width: 180, render: (v: string) => (v ? new Date(v).toLocaleString() : '-') },
          { title: '触发', dataIndex: 'triggerType', width: 90 },
          { title: '状态', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={v === 'success' ? 'green' : v === 'failed' ? 'red' : 'default'}>{v}</Tag> },
          { title: '耗时', dataIndex: 'durationMs', width: 80, render: (v: number) => `${v}ms` },
          { title: '错误', dataIndex: 'error', ellipsis: true },
        ]}
      />
      {runDetail && (
        <div style={{ marginTop: 12 }} data-testid="rule-run-detail">
          <Space>
            <Text strong>运行详情</Text>
            <Tag color={runDetail.run.status === 'success' ? 'green' : 'red'}>{runDetail.run.status}</Tag>
            <Button size="small" onClick={highlightRun} data-testid="rule-run-highlight">在画布高亮</Button>
          </Space>
          <Table
            size="small"
            style={{ marginTop: 8 }}
            rowKey={(_, i) => String(i)}
            pagination={false}
            dataSource={runDetail.logs || []}
            columns={[
              { title: '节点', dataIndex: 'nodeId', width: 130 },
              { title: '类型', dataIndex: 'nodeType', width: 90 },
              { title: '状态', dataIndex: 'status', width: 80, render: (v: string) => <Tag color={v === 'success' ? 'green' : v === 'failed' ? 'red' : 'default'}>{v}</Tag> },
              { title: '耗时', dataIndex: 'durationMs', width: 70, render: (v: number) => `${v}ms` },
              { title: '错误', dataIndex: 'error', ellipsis: true },
            ]}
            expandable={{
              expandedRowRender: (r: any) => (
                <pre style={{ fontSize: 12, maxHeight: 180, overflow: 'auto', margin: 0 }}>{pretty({ input: r.input, output: r.output })}</pre>
              ),
            }}
          />
        </div>
      )}
    </div>
  );

  const runAiDraft = async () => {
    if (!aiRequirement.trim()) {
      message.warning('请先描述规则链需求');
      return;
    }
    let payload: unknown;
    if (aiPayload.trim()) {
      try {
        payload = JSON.parse(aiPayload);
      } catch (e: any) {
        message.error(`样例消息 JSON 非法：${e?.message || e}`);
        return;
      }
    }
    setAiLoading(true);
    setAiResult(null);
    try {
      const r = await aiDraftRuleChain({ requirement: aiRequirement.trim(), payload });
      setAiResult(r);
      if (!r.ok) message.warning((r.errors || ['生成失败']).join('；'));
    } catch (e: any) {
      message.error(`AI 生成失败：${e?.message || e}`);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiDraft = () => {
    if (!aiResult?.graph) return;
    setGraph(aiResult.graph);
    setAiOpen(false);
    message.success('已应用到画布，请保存草稿');
  };

  const canvasTab = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
      <div style={{ width: 128, flexShrink: 0 }} data-testid="rule-node-palette">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>拖入/点击添加</Text>
          <Button size="small" type="link" onClick={() => setMarketOpen(true)} data-testid="rule-node-market-open">节点市场</Button>
        </div>
        <Input size="small" allowClear placeholder="搜索节点..." value={paletteQuery} onChange={(e) => setPaletteQuery(e.target.value)} style={{ marginTop: 6 }} data-testid="rule-node-search" />
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PALETTE_CATEGORY_ORDER.map((cat) => {
            const q = paletteQuery.trim().toLowerCase();
            const items = NODE_PALETTE.filter((t) => t.category === cat && (!q || `${t.label} ${t.type} ${t.kind || ''}`.toLowerCase().includes(q)));
            if (!items.length) return null;
            return (
              <div key={cat}>
                <Text type="secondary" style={{ fontSize: 11 }}>{PALETTE_CATEGORY_LABEL[cat] || cat}</Text>
                <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 4 }}>
                  {items.map((t) => (
                    <div
                      key={`${t.type}-${t.kind || 'default'}-${t.label}`}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('application/rule-node', JSON.stringify({ type: t.type, kind: t.kind }))}
                      onClick={() => addNode(t.type, t.kind)}
                      data-testid={t.kind ? `rule-palette-${t.type}-${t.kind}` : `rule-palette-${t.type}`}
                      style={{
                        border: '1px solid rgba(255,255,255,0.14)',
                        borderRadius: 6,
                        padding: '5px 7px',
                        cursor: 'grab',
                        background: 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <Tag color={t.color} style={{ marginInlineEnd: 4 }}>{t.kind || t.type}</Tag>
                      <Text style={{ fontSize: 12 }}>{t.label}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <RuleChainCanvas
          key={chain?.id || 'none'}
          graph={graphObj}
          onChange={setGraph}
          testResult={testResult}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          onSelectNode={(id) => { setSelectedNodeId(id); setSelectedEdgeId(null); }}
          onSelectEdge={(id) => { setSelectedEdgeId(id); setSelectedNodeId(null); }}
        />
      </div>
      <div style={{ width: 280, flexShrink: 0, maxHeight: 560, overflow: 'auto' }} data-testid="rule-node-config-panel">
        <RuleNodeConfigPanel
          graph={graphObj}
          nodeId={selectedNodeId}
          edgeId={selectedEdgeId}
          onChange={setGraph}
          onDeleted={() => { setSelectedNodeId(null); setSelectedEdgeId(null); }}
        />
      </div>
    </div>
  );

  const jsonTab = (
    <div>
      <Space style={{ marginBottom: 6 }} wrap>
        <Text type="secondary" style={{ fontSize: 12 }}>高级：直接编辑图 JSON（保存前可先「应用到画布」预览）</Text>
        <Button
          size="small"
          onClick={() => {
            const g = parseGraph();
            if (g) {
              setGraphObj(g);
              message.success('已应用到画布');
            }
          }}
          data-testid="rule-chain-apply-json"
        >
          应用到画布
        </Button>
      </Space>
      <TextArea
        value={graphText}
        onChange={(e) => setGraphText(e.target.value)}
        rows={22}
        spellCheck={false}
        style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}
        data-testid="rule-chain-graph"
      />
    </div>
  );

  if (paid === false) {
    return (
      <div style={{ padding: 32 }} data-testid="rule-chains-paid-gate">
        <Result
          status="403"
          title="规则链为 Pro 及以上套餐功能"
          subTitle="升级套餐后可使用规则链编排、试跑、发布与运行历史。"
          extra={
            <Button type="primary" onClick={() => usePopupStore.getState().openUpgrade()}>
              升级套餐
            </Button>
          }
        />
      </div>
    );
  }

  if (paid === null) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }} data-testid="rule-chains-loading">
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }} data-testid="rule-chains-page">
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>规则链</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            多步骤消息流：source → filter/transform/enrich → action → route；发布后由设备状态触发。
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} data-testid="rule-chain-create">
            新建规则链
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="规则链 P3-2：拖拽画布（节点面板/连线/配置/试跑高亮）+ JSON 高级编辑；试跑使用已保存草稿。"
      />

      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={false}
        data-testid="rule-chains-table"
        columns={[
          { title: '名称', dataIndex: 'name' },
          {
            title: '启用',
            dataIndex: 'enabled',
            width: 90,
            render: (v: boolean, row: RuleChainItem) => <Switch size="small" checked={v} disabled={!canPublish} onChange={(x) => toggle(row, x)} title={canPublish ? undefined : '只读角色不可启用'} />,
          },
          {
            title: '状态',
            width: 120,
            render: (_: unknown, row: RuleChainItem) => (
              <Space size={4}>
                {row.currentVersionId ? <Tag color="green">已发布</Tag> : <Tag>未发布</Tag>}
              </Space>
            ),
          },
          { title: '更新时间', dataIndex: 'updatedAt', width: 180, render: (v: string) => (v ? new Date(v).toLocaleString() : '-') },
          {
            title: '操作',
            width: 160,
            render: (_: unknown, row: RuleChainItem) => (
              <Space>
                <Button size="small" onClick={() => openEditor(row.id)} data-testid={`rule-chain-edit-${row.id}`}>编辑</Button>
                <Popconfirm title="删除规则链？" onConfirm={async () => { await deleteRuleChain(row.id); await load(); }}>
                  <Button size="small" danger>删除</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Drawer
        title={chain ? `规则链 · ${chain.name}` : '规则链'}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        width={1200}
        destroyOnHidden
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              当前：{current ? `v${current.version}` : '未发布'}{draft ? ` · 草稿 v${draft.version}` : ''}
            </Text>
            <Button icon={<RobotOutlined />} onClick={() => { setAiResult(null); setAiOpen(true); }} data-testid="rule-chain-ai">AI 生成</Button>
            <Button icon={<SaveOutlined />} loading={busy} onClick={saveDraft} data-testid="rule-chain-save">保存草稿</Button>
            <Button icon={<PlayCircleOutlined />} loading={busy} onClick={runTest} data-testid="rule-chain-test">试跑</Button>
            <Button type="primary" icon={<RocketOutlined />} loading={busy} disabled={!canPublish} onClick={publish} data-testid="rule-chain-publish" title={canPublish ? undefined : '只读角色不可发布'}>发布</Button>
          </Space>
        }
      >
        {!chain ? (
          <Empty />
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
              <Text>启用</Text>
              <Switch
                checked={chain.enabled}
                disabled={!canPublish}
                data-testid="rule-chain-enabled"
                onChange={async (v) => {
                  await updateRuleChain(chain.id, { enabled: v });
                  setChain({ ...chain, enabled: v });
                }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>发布 + 启用后，设备状态消息会触发该链</Text>
            </Space>

            <Tabs
              activeKey={tab}
              onChange={(k) => { const next = k as 'canvas' | 'json' | 'runs'; setTab(next); if (next === 'runs') void loadRuns(); }}
              items={[
                { key: 'canvas', label: <span><ApartmentOutlined /> 画布</span>, children: canvasTab },
                { key: 'json', label: <span><CodeOutlined /> JSON</span>, children: jsonTab },
                { key: 'runs', label: '运行历史', children: runsTab },
              ]}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <Text strong>样例报文（试跑）</Text>
                <TextArea
                  value={payloadText}
                  onChange={(e) => setPayloadText(e.target.value)}
                  rows={7}
                  spellCheck={false}
                  style={{ fontFamily: 'Consolas, monospace', fontSize: 12, marginTop: 4 }}
                  data-testid="rule-chain-payload"
                />
                <Space style={{ marginTop: 8 }}>
                  <Select
                    placeholder="选择回滚版本"
                    style={{ width: 180 }}
                    value={rollbackId}
                    onChange={setRollbackId}
                    options={chain.versions.filter((v) => v.status !== 'draft').map((v) => ({ value: v.id, label: `v${v.version}（${v.status}）` }))}
                  />
                  <Button icon={<RollbackOutlined />} disabled={!rollbackId || !canPublish} onClick={rollback} data-testid="rule-chain-rollback">回滚</Button>
                </Space>
              </div>
              <div>
                {testResult ? (
                  <div data-testid="rule-chain-test-result">
                    <Alert
                      type={testResult.ok ? 'success' : 'error'}
                      showIcon
                      message={testResult.ok ? `试跑通过（${testResult.durationMs}ms）` : `试跑失败：${testResult.error || ''}`}
                    />
                    <Table
                      size="small"
                      style={{ marginTop: 8 }}
                      rowKey={(_, i) => String(i)}
                      pagination={false}
                      dataSource={testResult.nodeResults}
                      columns={[
                        { title: '节点', dataIndex: 'nodeId', width: 110 },
                        { title: '类型', dataIndex: 'type', width: 90 },
                        { title: '状态', dataIndex: 'status', width: 80, render: (v: string) => <Tag color={v === 'success' ? 'green' : v === 'failed' ? 'red' : 'default'}>{v}</Tag> },
                        { title: '耗时', dataIndex: 'durationMs', width: 70, render: (v: number) => `${v}ms` },
                        { title: '错误', dataIndex: 'error', ellipsis: true },
                      ]}
                      expandable={{
                        expandedRowRender: (r: any) => (
                          <pre style={{ fontSize: 12, maxHeight: 160, overflow: 'auto', margin: 0 }}>{pretty(r.output)}</pre>
                        ),
                      }}
                    />
                  </div>
                ) : (
                  <Empty description="点「试跑」查看逐节点结果（使用已保存草稿）" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </div>
            </div>

            <div>
              <Text strong>版本</Text>
              <Table
                size="small"
                style={{ marginTop: 6 }}
                rowKey="id"
                pagination={false}
                dataSource={chain.versions}
                columns={[
                  { title: '版本', dataIndex: 'version', width: 70, render: (v: number) => `v${v}` },
                  { title: '状态', dataIndex: 'status', width: 100, render: (v: string) => <Tag color={v === 'published' ? 'green' : v === 'draft' ? 'blue' : 'default'}>{v}</Tag> },
                  { title: '节点数', width: 80, render: (_: unknown, v: RuleChainVersionItem) => v.graph?.nodes?.length ?? 0 },
                  { title: '连线数', width: 80, render: (_: unknown, v: RuleChainVersionItem) => v.graph?.edges?.length ?? 0 },
                  { title: '创建时间', dataIndex: 'createdAt', render: (v: string) => (v ? new Date(v).toLocaleString() : '-') },
                  { title: '对比', width: 70, render: (_: unknown, v: RuleChainVersionItem) => <Button size="small" type="link" onClick={() => setDiffVersion(v)} data-testid={`rule-chain-diff-${v.version}`}>对比</Button> },
                ]}
              />
            </div>
          </Space>
        )}
      </Drawer>

      <Modal
        title="节点市场 · 内置节点"
        open={marketOpen}
        onCancel={() => setMarketOpen(false)}
        footer={<Button onClick={() => setMarketOpen(false)}>关闭</Button>}
        width={760}
        data-testid="rule-node-market-modal"
      >
        <Input allowClear placeholder="搜索节点..." value={marketQuery} onChange={(e) => setMarketQuery(e.target.value)} data-testid="rule-node-market-search" />
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          {NODE_PALETTE.filter((t) => {
            const q = marketQuery.trim().toLowerCase();
            return !q || `${t.label} ${t.type} ${t.kind || ''}`.toLowerCase().includes(q);
          }).map((t) => (
            <div key={`${t.type}-${t.kind || 'default'}-${t.label}`} style={{ border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6, padding: 8 }}>
              <div><Tag color={t.color}>{t.kind || t.type}</Tag><Text>{t.label}</Text></div>
              <Button size="small" type="link" onClick={() => addNode(t.type, t.kind)} data-testid={`rule-market-add-${t.type}-${t.kind || 'default'}`}>添加到画布</Button>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        title={diffVersion ? `版本对比 · v${diffVersion.version} → 当前` : '版本对比'}
        open={!!diffVersion}
        onCancel={() => setDiffVersion(null)}
        footer={<Button onClick={() => setDiffVersion(null)}>关闭</Button>}
        width={720}
        data-testid="rule-chain-diff-modal"
      >
        {diffVersion && (() => {
          const d = graphDiff(diffVersion.graph, draft?.graph || current?.graph || EMPTY_GRAPH);
          return (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text>节点：新增 {d.added.length} / 删除 {d.removed.length} / 修改 {d.changed.length}</Text>
              <Text>连线：新增 {d.edgesAdded.length} / 删除 {d.edgesRemoved.length}</Text>
              {!!d.added.length && <div>新增节点：{d.added.map((id) => <Tag key={id} color="green">{id}</Tag>)}</div>}
              {!!d.removed.length && <div>删除节点：{d.removed.map((id) => <Tag key={id} color="red">{id}</Tag>)}</div>}
              {!!d.changed.length && <div>修改节点：{d.changed.map((id) => <Tag key={id} color="orange">{id}</Tag>)}</div>}
              {!!d.edgesAdded.length && <div>新增连线：{d.edgesAdded.map((k) => <Tag key={k} color="green">{k}</Tag>)}</div>}
              {!!d.edgesRemoved.length && <div>删除连线：{d.edgesRemoved.map((k) => <Tag key={k} color="red">{k}</Tag>)}</div>}
            </Space>
          );
        })()}
      </Modal>

      <Modal
        title="AI 生成规则链"
        open={aiOpen}
        onCancel={() => setAiOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setAiOpen(false)}>取消</Button>,
          <Button key="gen" loading={aiLoading} onClick={runAiDraft} data-testid="rule-chain-ai-generate">生成</Button>,
          <Button key="apply" type="primary" disabled={!aiResult?.ok || !aiResult?.graph} onClick={applyAiDraft} data-testid="rule-chain-ai-apply">应用到画布</Button>,
        ]}
        data-testid="rule-chain-ai-modal"
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          描述触发/条件/动作，例如：设备状态 data.v&gt;5 时广播 rulechain:alert。
        </Text>
        <TextArea
          rows={4}
          value={aiRequirement}
          onChange={(e) => setAiRequirement(e.target.value)}
          placeholder="设备状态 data.v>5 时广播 rulechain:alert"
          style={{ marginTop: 8 }}
          data-testid="rule-chain-ai-requirement"
        />
        <Text strong style={{ display: 'block', marginTop: 8 }}>样例消息（可选，用于自动试跑）</Text>
        <TextArea
          rows={4}
          value={aiPayload}
          onChange={(e) => setAiPayload(e.target.value)}
          style={{ fontFamily: 'Consolas, monospace', fontSize: 12, marginTop: 4 }}
          data-testid="rule-chain-ai-payload"
        />
        {aiResult && (
          <div style={{ marginTop: 10 }} data-testid="rule-chain-ai-result">
            <Alert
              type={aiResult.ok ? 'success' : 'error'}
              showIcon
              message={aiResult.ok ? `生成成功（尝试 ${aiResult.attempts ?? 1} 次）` : `生成失败：${(aiResult.errors || []).join('；')}`}
            />
            {aiResult.explanation && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>{aiResult.explanation}</Text>
            )}
            {!!aiResult.warnings?.length && (
              <Alert type="warning" showIcon style={{ marginTop: 6 }} message={aiResult.warnings.join('；')} />
            )}
            {aiResult.graph && (
              <div style={{ marginTop: 6 }}>
                <Text strong>{aiResult.graph.nodes?.length ?? 0} 节点 / {aiResult.graph.edges?.length ?? 0} 连线</Text>
                <Space wrap size={4} style={{ marginLeft: 8 }}>
                  {(aiResult.graph.nodes || []).map((n) => <Tag key={n.id}>{n.type}</Tag>)}
                </Space>
              </div>
            )}
            {!!aiResult.testResult?.nodeResults?.length && (
              <Table
                size="small"
                style={{ marginTop: 6 }}
                rowKey={(_, i) => String(i)}
                pagination={false}
                dataSource={aiResult.testResult.nodeResults}
                columns={[
                  { title: '节点', dataIndex: 'nodeId', width: 100 },
                  { title: '类型', dataIndex: 'type', width: 80 },
                  { title: '状态', dataIndex: 'status', width: 70, render: (v: string) => <Tag color={v === 'success' ? 'green' : 'red'}>{v}</Tag> },
                  { title: '错误', dataIndex: 'error', ellipsis: true },
                ]}
              />
            )}
          </div>
        )}
      </Modal>

      <Drawer
        title="新建规则链"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={420}
        extra={
          <Button
            type="primary"
            disabled={!name.trim()}
            onClick={async () => {
              try {
                const c = await createRuleChain({ name: name.trim(), description: desc.trim() || undefined });
                setCreateOpen(false);
                setName('');
                setDesc('');
                await load();
                await openEditor(c.id);
              } catch (e: any) {
                message.error(e?.message || '创建失败');
              }
            }}
            data-testid="rule-chain-create-submit"
          >
            创建
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input placeholder="名称（必填）" value={name} onChange={(e) => setName(e.target.value)} data-testid="rule-chain-name" />
          <TextArea placeholder="描述（可选）" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} />
        </Space>
      </Drawer>
    </div>
  );
}
